/* istanbul ignore file */
/* tslint:disable */
/* eslint-disable */
import type { Scheduler } from '../models/Scheduler';
import type { SchedulerDaten } from '../models/SchedulerDaten';
import type { SchedulerListe } from '../models/SchedulerListe';
import type { SchedulerScope } from '../models/SchedulerScope';

import type { CancelablePromise } from '../core/CancelablePromise';
import type { BaseHttpRequest } from '../core/BaseHttpRequest';

export class SchedulersService {

  constructor(public readonly httpRequest: BaseHttpRequest) {}

  /**
   * Scheduler suchen
   * Sucht nach Schedulern
   * @returns SchedulerListe Liste der gesuchten Scheduler
   * @throws ApiError
   */
  public searchSchedulers({
    search,
    scope,
    mandantId,
    selectableForAufgabe,
    selectableForMandant,
    aufgabeId,
    filtered,
    page,
    limit,
    sort,
  }: {
    /**
     * Suchstring für Scheduler (Name)
     */
    search?: string,
    /**
     * Filtert nach Gültigkeitsbereich
     */
    scope?: SchedulerScope,
    /**
     * Filtert nach Mandant (nur relevant für scope MANDANT/AUFGABE)
     */
    mandantId?: string,
    /**
     * Wenn gesetzt, werden nur Scheduler zurückgegeben, die für diese Aufgabe auswählbar sind (GLOBAL, der zugehörige Mandant oder die Aufgabe selbst)
     */
    selectableForAufgabe?: string,
    /**
     * Wenn gesetzt (Aufgabe existiert noch nicht), werden nur Scheduler zurückgegeben, die für eine neue Aufgabe dieses Mandanten auswählbar wären (GLOBAL oder der angegebene Mandant). Wird ignoriert, wenn selectableForAufgabe gesetzt ist.
     */
    selectableForMandant?: string,
    /**
     * Filtert nach der Aufgabe, welcher der Scheduler gehört (nur relevant für scope AUFGABE)
     */
    aufgabeId?: string,
    /**
     * Filterstring für Scheduler Seite
     */
    filtered?: string,
    /**
     * Seitenzahl der Scheduler Seite
     */
    page?: number,
    /**
     * Limit der Liste von Scheduler Einträgen
     */
    limit?: number,
    /**
     * Sortierung der Scheduler Seite
     */
    sort?: string,
  }): CancelablePromise<SchedulerListe> {
    return this.httpRequest.request({
      method: 'GET',
      url: '/api/v1/schedulers',
      query: {
        'search': search,
        'scope': scope,
        'mandantId': mandantId,
        'selectableForAufgabe': selectableForAufgabe,
        'selectableForMandant': selectableForMandant,
        'aufgabeId': aufgabeId,
        'filtered': filtered,
        'page': page,
        'limit': limit,
        'sort': sort,
      },
      errors: {
        401: `Unauthorized for this request (searchSchedulers)`,
        403: `Forbidden for this request (searchSchedulers)`,
      },
    });
  }

  /**
   * Einen Scheduler erstellen
   * Erstellt einen Scheduler
   * @returns Scheduler Der angelegte Scheduler
   * @throws ApiError
   */
  public createScheduler({
    requestBody,
  }: {
    /**
     * Daten, mit welchen der Scheduler angelegt werden soll
     */
    requestBody: SchedulerDaten,
  }): CancelablePromise<Scheduler> {
    return this.httpRequest.request({
      method: 'POST',
      url: '/api/v1/schedulers',
      body: requestBody,
      mediaType: 'application/json',
      errors: {
        400: `Ungültige Eingabe`,
        401: `Unauthorized for this request (createScheduler)`,
        403: `Forbidden for this request (createScheduler)`,
      },
    });
  }

  /**
   * Einen Scheduler bekommen
   * Bekomme einen Scheduler
   * @returns Scheduler Der Scheduler
   * @throws ApiError
   */
  public getScheduler({
    schedulerId,
  }: {
    /**
     * Eindeutige ID des Schedulers
     */
    schedulerId: string,
  }): CancelablePromise<Scheduler> {
    return this.httpRequest.request({
      method: 'GET',
      url: '/api/v1/schedulers/{schedulerId}',
      path: {
        'schedulerId': schedulerId,
      },
      errors: {
        401: `Unauthorized for this request (getScheduler)`,
        403: `Forbidden for this request (getScheduler)`,
        404: `Not Found for this request (getScheduler)`,
      },
    });
  }

  /**
   * Einen Scheduler aktualisieren
   * Aktualisiert einen Scheduler
   * @returns Scheduler Der aktualisierte Scheduler
   * @throws ApiError
   */
  public updateScheduler({
    schedulerId,
    requestBody,
  }: {
    /**
     * Eindeutige ID des Schedulers
     */
    schedulerId: string,
    /**
     * Daten, mit welchen der Scheduler aktualisiert werden soll
     */
    requestBody: SchedulerDaten,
  }): CancelablePromise<Scheduler> {
    return this.httpRequest.request({
      method: 'PUT',
      url: '/api/v1/schedulers/{schedulerId}',
      path: {
        'schedulerId': schedulerId,
      },
      body: requestBody,
      mediaType: 'application/json',
      errors: {
        400: `Ungültige Eingabe`,
        401: `Unauthorized for this request (updateScheduler)`,
        403: `Forbidden for this request (updateScheduler)`,
        404: `Not Found for this request (updateScheduler)`,
      },
    });
  }

  /**
   * Einen Scheduler löschen
   * Löscht einen Scheduler
   * @returns Scheduler Der gelöschte Scheduler
   * @throws ApiError
   */
  public deleteScheduler({
    schedulerId,
  }: {
    /**
     * Eindeutige ID des Schedulers
     */
    schedulerId: string,
  }): CancelablePromise<Scheduler> {
    return this.httpRequest.request({
      method: 'DELETE',
      url: '/api/v1/schedulers/{schedulerId}',
      path: {
        'schedulerId': schedulerId,
      },
      errors: {
        401: `Unauthorized for this request (deleteScheduler)`,
        403: `Forbidden for this request (deleteScheduler)`,
        404: `Not Found for this request (deleteScheduler)`,
        409: `Conflict for this request (deleteScheduler) - Scheduler wird noch von einer Aufgabe verwendet`,
      },
    });
  }

}
